import {
  FlagRestrictParams,
  ResourceSearchParams,
  RestrictSearchParams,
  SearchFilterCountResponse,
  SearchFilterRequest,
} from "@modules/resource-search/resource-search-types.ts";
import axios from "axios";
import {
  BROWSE_RESULT_PAGE_SIZE,
  getFilterOptions,
  populateFilter,
  populateSearchApiData,
  populateSearchFilterCountApiData,
  RESOURCE_DOMAIN,
  RESOURCE_SEARCH_TYPE,
  SEARCH_RESULT_PAGE_SIZE,
  SORT_BY_TO_ORDER,
  SORT_OPTIONS,
  SORT_OPTIONS_CONFIG,
} from "@modules/resource-search/resource-search-helper.ts";
import SearchApi from "@apis/search.api.ts";
import { computed, nextTick, Ref, ref } from "vue";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useI18n } from "vue-i18n";
import { useSearchResult } from "@modules/search/composables/use-search-result.ts";
import SearchUtils from "@modules/search/service/search.utils.ts";
import { isEmpty } from "@utils/var.check.ts";
import { debounceFn } from "@/utils/debounce";
import ScrollUtils from "@utils/scroll.utils.ts";
import DOMUtils from "@utils/dom.utils.ts";

export const useAddFromLibrarySubpage = (
  param: Ref<ResourceSearchParams>,
  restriction: RestrictSearchParams,
  flagRestrictParams: FlagRestrictParams, //used by App and Media
  triggerSearch: () => Promise<void>,
  triggerSearchAndAggregateFilters: () => Promise<void>,
) => {
  const selectedFilter = ref<Record<string, string[]>>({});
  const resettingKeyword = ref(false);
  const { t } = useI18n();
  const { passiveApiFailHandler } = useErrorHandler();
  const {
    isLoading,
    pageNo,
    pageSize,
    searchResult,
    disableForwardsButton,
    disableBackwardsButton,
    totalRecordLabel,
  } = useSearchResult(param.value.pageSize);

  const cancelSource = ref<ReturnType<typeof axios.CancelToken.source> | null>(null);

  const searchFilterCountResult = ref<SearchFilterCountResponse>({});

  const hasKeyword = computed(() => {
    return resettingKeyword.value || SearchUtils.hasSearchKeyword(param.value.keyword);
  });

  const showPagination = computed(() => {
    return !isEmptySearchResult.value;
  });

  const sortByOptions = computed(() => {
    return SORT_OPTIONS[param.value.resource].map((option: { name: string; value: string }) => ({
      name: t(option.name),
      value: option.value,
    }));
  });

  const isEmptyFilterSelection = computed(() =>
    Object.values(selectedFilter.value ?? {}).every((arr) => arr.length === 0),
  );

  const isPersonalResourceRestriction = computed(
    () =>
      restriction.restrictLocation?.length === 1 &&
      restriction.restrictLocation[0] === RESOURCE_DOMAIN.PERSONAL,
  );

  const isAppResourceSearchType = computed(() => param.value.resource === RESOURCE_SEARCH_TYPE.APP);

  const isWebsiteResourceSearchType = computed(
    () => param.value.resource === RESOURCE_SEARCH_TYPE.WEBSITE,
  );

  const emptyBrowseResultMessage = computed(() => {
    if (isPersonalResourceRestriction.value) {
      return t("browse_resource_in_my_drive_no_resources_title");
    } else if (isAppResourceSearchType.value) {
      return t("browse_app_list_no_result_message");
    } else if (isWebsiteResourceSearchType.value) {
      return t("browse_external_resource_list_no_result_message");
    } else {
      return t("browse_resource_list_no_result_message");
    }
  });

  const emptyBrowseResultSubMessage = computed(() =>
    isPersonalResourceRestriction.value
      ? t("browse_resource_in_my_drive_no_resources_sub_message")
      : "",
  );

  const emptySearchResultMessage = computed(() => {
    return t("search_result_list_no_result_message");
  });

  const emptySearchResultSubMessage = computed(() =>
    isEmptyFilterSelection.value
      ? t("search_result_list_no_result_try_changing_search_terms_sub_message")
      : t("search_result_list_no_result_try_changing_search_filters_sub_message"),
  );

  const isEmptySearchResult = computed(() => {
    if (!searchResult.value) {
      return true;
    }
    return searchResult.value.data && searchResult.value.data.length === 0;
  });

  const showFilterAccordion = computed(
    () => isLoading.value || !(isEmptySearchResult.value && isEmptyFilterSelection.value),
  );

  const filterOptions = computed(() =>
    getFilterOptions(param.value.resource, restriction, searchFilterCountResult.value),
  );

  const cancelSearch = () => {
    if (cancelSource.value) {
      cancelSource.value.cancel("Start new search, stop active search");
    }
  };

  const fetchSearchData = async () => {
    cancelSearch();
    cancelSource.value = axios.CancelToken.source();
    //resetSearchData();

    try {
      const requestData = populateSearchApiData(
        param.value.resource,
        param.value.keyword,
        populateFilter(param.value, param.value.resource, restriction),
        param.value.sortBy ?? null,
        param.value.pageNo,
        param.value.pageSize,
      );
      if (flagRestrictParams.fromResourceLibrary) {
        requestData.fromResourceLibrary = flagRestrictParams.fromResourceLibrary;
      }
      if (flagRestrictParams.fromMoeLibrary) {
        requestData.fromMoeLibrary = flagRestrictParams.fromMoeLibrary;
      }
      if (flagRestrictParams.checkCopyRightForMOEResource) {
        requestData.checkCopyRightForMOEResource = flagRestrictParams.checkCopyRightForMOEResource;
      }
      const response = await SearchApi.search(requestData, cancelSource.value.token);
      searchResult.value = response["searchResult"][param.value.resource];
    } catch (error) {
      if (!axios.isCancel(error)) {
        passiveApiFailHandler();
      }
    } finally {
      cancelSource.value = null;
    }
  };

  const fetchSearchFilterCountData = async () => {
    try {
      const requestData = populateSearchFilterCountApiData(
        param.value.resource,
        param.value.keyword,
        param.value as SearchFilterRequest,
        restriction,
      );
      const response = await SearchApi.searchFilterCount(requestData);
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }

      searchFilterCountResult.value = response.data;

      //populate selected filter for filter accordion checkbox
      selectedFilter.value = filterOptions.value.reduce(
        (acc, filter) => {
          const field = filter.field;
          const selectedValues = param.value[field];
          if (selectedValues?.length) {
            acc[field] = selectedValues;
          }
          return acc;
        },
        {} as Record<string, string[]>,
      );
    } catch (error) {
      passiveApiFailHandler(error);
    }
  };

  const resetFilter = () => {
    param.value = {
      ...param.value,
      authors: [],
      learningOutcome: [],
      level: [],
      location: [],
      parentTopic: [],
      resourceType: [],
      subject: [],
      syllabus: "",
      topic: [],
    };
  };
  const resetSortBy = () => {
    param.value = {
      ...param.value,
      sortBy: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
      sortByOrder: "",
    };
  };
  const backToTop = () => {
    nextTick(() => {
      if (!isEmptySearchResult.value) {
        ScrollUtils.scrollToTopOfContainer(DOMUtils.el(".add-from-library-content-subpage .main"));
      }
    });
  };
  const resetKeyword = () => {
    if (isEmpty(param.value.keyword)) {
      return;
    }
    resettingKeyword.value = true;
    const temp = param.value.keyword;
    param.value.keyword = "";
    nextTick(() => {
      param.value.keyword = temp;
      resettingKeyword.value = false;
    });
  };
  const handleKeywordSearch = ({ keyword }) => {
    param.value.keyword = keyword;
    param.value.pageNo = 1; // reset to first page on new search
    pageNo.value = 1;
    if (hasKeyword.value) {
      param.value.pageSize = SEARCH_RESULT_PAGE_SIZE;
      pageSize.value = SEARCH_RESULT_PAGE_SIZE;
    } else {
      param.value.pageSize = BROWSE_RESULT_PAGE_SIZE;
      pageSize.value = BROWSE_RESULT_PAGE_SIZE;
    }
    //reset filter and sortBy
    resetFilter();
    resetSortBy();
    //trigger search and filter API
    let promise;
    if (hasKeyword.value) {
      promise = triggerSearchAndAggregateFilters();
    } else {
      promise = triggerSearch();
    }
    promise.then(() => {
      backToTop();
    });
  };
  const handleSortByChange = (event: string): void => {
    param.value.sortBy = event;
    param.value.sortByOrder = SORT_BY_TO_ORDER[event];
    triggerSearch().then(() => {
      resetKeyword();
      backToTop();
    });
  };

  const handlePageChange = (event: { page: number }) => {
    // case where page onload then cv-pagination component is mounted again
    if (param.value.pageNo === event.page) return;
    param.value.pageNo = event.page;
    pageNo.value = event.page;
    triggerSearch().then(() => {
      resetKeyword();
      backToTop();
    });
  };

  const handleFilterChanged = (filterData: Record<string, string[]>) => {
    if (!selectedFilter.value) {
      selectedFilter.value = {};
    }

    const filterMap = Object.fromEntries(
      Object.entries(filterData),
    ) as unknown as SearchFilterRequest;
    param.value = {
      ...param.value,
      ...filterMap,
    };
    param.value.pageNo = 1;
    pageNo.value = 1;
    triggerSearchAndAggregateFiltersDebounce();
  };

  const triggerSearchAndAggregateFiltersDebounce = debounceFn(
    function inner() {
      triggerSearchAndAggregateFilters().then(() => {
        resetKeyword();
        backToTop();
      });
    },
    1000,
    {
      leading: false,
      trailing: true,
    },
  );

  return {
    isLoading,
    isPersonalResourceRestriction,
    isEmptySearchResult,
    searchResult,
    showFilterAccordion,
    showPagination,
    hasKeyword,
    fetchSearchData,
    fetchSearchFilterCountData,
    disableForwardsButton,
    disableBackwardsButton,
    totalRecordLabel,
    selectedFilter,
    filterOptions,
    sortByOptions,
    emptyBrowseResultMessage,
    emptyBrowseResultSubMessage,
    emptySearchResultMessage,
    emptySearchResultSubMessage,
    handleFilterChanged,
    handlePageChange,
    handleSortByChange,
    handleKeywordSearch,
  };
};
